import { toast } from "./toast.js";
import {
  deleteItem,
  reorderZone,
  setItemPublished,
} from "../api/member-profile/member-profile.js";

/**
 * The actions on a member profile zone list: publish, unpublish, delete and reorder.
 *
 * Progressive enhancement, like social-link-rows.js. Editing an item is a plain link and works
 * with scripting off; these three do not, because each is a state change that would otherwise need
 * a form and a full page round trip per row. That limit is the same one the cover image upload
 * already has, and it is recorded in the ZoneList view.
 *
 * Everything it needs comes off data- attributes the view renders. Nothing here knows a cap, a
 * limit or a URL: the server owns all three and refuses independently, so this only has to ask.
 */

const ZONE_SELECTOR = "[data-testid='zone-list']";

/**
 * Reloads to show the new state. Deliberately a reload rather than patching the DOM: publishing
 * changes the row, deleting removes it, and reordering renumbers every position after it - three
 * different partial updates to keep correct against a list the server has just rewritten. The
 * page is small and behind a login, so the round trip is cheap and always right.
 */
const refresh = () => window.location.reload();

// Long enough to read a one-line toast before the page it sits on goes away.
const FAILURE_RELOAD_DELAY_MS = 2500;

const reportFailure = (result) => {
  const message =
    result.message ??
    Object.values(result.errors ?? {})[0] ??
    "Sorry, that could not be done just now. Please try again shortly.";

  toast.error(message);
};

const bindPublish = (zone) => {
  zone.querySelectorAll(".js-zone-publish").forEach((button) => {
    button.addEventListener("click", async () => {
      button.disabled = true;

      const result = await setItemPublished(
        button.dataset.itemKey,
        button.dataset.publish === "true"
      );

      if (result.isSuccess) {
        refresh();
        return;
      }

      button.disabled = false;
      reportFailure(result);
    });
  });
};

const bindDelete = (zone) => {
  zone.querySelectorAll(".js-zone-delete").forEach((button) => {
    button.addEventListener("click", async () => {
      // Deleting takes the item's images and video with it, server side, and nothing here can
      // undo that - so it asks first. The item's own name is in the question, because "are you
      // sure" on a list of six tells the member nothing about which one they are about to lose.
      const name = button.dataset.itemName;

      if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) {
        return;
      }

      button.disabled = true;

      // Deleting an item with a video in it can take a few seconds while its media goes too.
      // The row says it is on its way out so the member does not assume nothing happened.
      const row = button.closest("[data-testid='zone-item']");
      row?.classList.add("is-deleting");
      row?.setAttribute("aria-busy", "true");

      const result = await deleteItem(button.dataset.itemKey);

      if (result.isSuccess) {
        refresh();
        return;
      }

      // Reload whatever the answer was. A failure can still have removed the item (the content
      // goes before its media), and "not found" means it is already gone - either way the list
      // on screen is stale, and the member should not have to refresh to find out. The toast
      // gets a moment on screen first.
      reportFailure(result);
      window.setTimeout(refresh, FAILURE_RELOAD_DELAY_MS);
    });
  });
};

/**
 * Reordering, one position at a time, through the up and down controls the list renders.
 *
 * Explicit controls rather than drag: they are the ones that work with a pointer, a keyboard and a
 * screen reader without a drag library and a parallel keyboard implementation to keep in step.
 *
 * The whole order is sent, not a position. The server moves the keys it is given to the front in
 * that order and leaves the rest behind them, so a stale list cannot scramble what it does not
 * mention - which is what makes reordering and then deleting safe.
 */
const bindReorder = (zone) => {
  const buttons = zone.querySelectorAll(".js-zone-move");

  if (buttons.length === 0) {
    return;
  }

  const list = zone.querySelector("[data-testid='zone-items']");

  const move = async (button, item, delta) => {
    const items = [...list.children];
    const from = items.indexOf(item);
    const to = from + delta;

    if (to < 0 || to >= items.length) {
      return;
    }

    items.splice(from, 1);
    items.splice(to, 0, item);

    const result = await reorderZone(
      zone.dataset.zone,
      items.map((row) => row.dataset.itemKey)
    );

    if (result.isSuccess) {
      refresh();
      return;
    }

    // Nothing moved, so put the control back rather than leaving a row that cannot be tried again.
    // Only the one that was pressed: the first row's up and the last row's down render disabled
    // and must stay that way.
    button.disabled = false;

    reportFailure(result);
  };

  buttons.forEach((button) => {
    const item = button.closest("[data-testid='zone-item']");

    button.addEventListener("click", () => {
      button.disabled = true;
      move(button, item, button.dataset.direction === "up" ? -1 : 1);
    });
  });
};

export default function initMemberProfileZone() {
  document.querySelectorAll(ZONE_SELECTOR).forEach((zone) => {
    bindPublish(zone);
    bindDelete(zone);
    bindReorder(zone);
  });
}
